import { BillingCycle, LicensePlan } from '@/common/types/organisation';
import {
  formatPrice,
  getPlanIcon,
  MULTI_YEAR_DISCOUNTS,
  PLAN_DETAILS,
} from '@/common/utils/license';
import tw from '@/common/utils/twmacro';
import { DevicesIcon, StudentIcon, UserIcon } from '@phosphor-icons/react';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';
import { cn } from '@shadcn/lib/utils';
import { Check } from 'lucide-react';
import { useMemo } from 'react';

interface PlanPricingProps {
  plan: LicensePlan;
  billingCycle: BillingCycle;
  currency: 'USD' | 'AUD' | 'NZD';
  recommended?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}
export default function PlanPricing({
  plan,
  billingCycle,
  currency,
  recommended,
  selected,
  onSelect,
}: PlanPricingProps) {
  const planDetails = PLAN_DETAILS[plan];

  const { basePrice, additionalSeatPrice } = useMemo(() => {
    return billingCycle === 'monthly'
      ? {
          basePrice: 20,
          additionalSeatPrice: 6.66,
        }
      : {
          basePrice:
            planDetails.price * (1 - MULTI_YEAR_DISCOUNTS[billingCycle]),
          additionalSeatPrice:
            planDetails.additionalSeatPrice *
            (1 - MULTI_YEAR_DISCOUNTS[billingCycle]),
        };
  }, [billingCycle, planDetails]);

  return (
    <ProductContainer
      className={cn(selected && 'border-primary shadow-lg')}
      onClick={onSelect}
    >
      {selected && (
        <div className="absolute top-0 right-0 h-full w-full bg-primary opacity-10 z-0" />
      )}
      {recommended && (
        <Badge className="absolute -top-2 -right-2">
          <Check size={16} className="mr-2" />
          <span>Best Value</span>
        </Badge>
      )}
      <ProductHeader>
        <img
          src={getPlanIcon(plan)}
          alt={planDetails.name}
          className="w-14 h-14 mt-2"
        />
        <span className="text-2xl xl:text-xl font-semibold">
          {planDetails.name}
        </span>
        <span className="text-sm text-muted-foreground">
          {planDetails.description}
        </span>
      </ProductHeader>
      <ProductPrice>
        <div className="flex flex-row items-end gap-1">
          <span className="text-2xl md:text-3xl font-bold">
            {formatPrice(basePrice, currency, true, false)}
          </span>
          <span className="text-xs text-muted-foreground font-light mb-1">
            {billingCycle === 'monthly' ? '/mo' : '/yr'}
          </span>
        </div>
        <span className="text-xs text-muted-foreground font-light">
          plus {formatPrice(additionalSeatPrice, currency)} per additional
          learner per {billingCycle === 'monthly' ? 'month' : 'year'}
        </span>
      </ProductPrice>
      <ProductFeatures>
        <ProductFeatureRow>
          <StudentIcon size={20} />
          <span className="font-semibold">
            {planDetails.seatCount} learner accounts
          </span>
        </ProductFeatureRow>
        <Separator className="my-2" />
        <ProductFeatureRow className="font-light">
          <UserIcon size={18} />
          <span>Unlimited educators</span>
        </ProductFeatureRow>
        <Separator className="my-2" />
        <ProductFeatureRow className="font-light">
          <DevicesIcon size={18} />
          <span>Unlimited devices</span>
        </ProductFeatureRow>
      </ProductFeatures>
      <Button
        variant={selected ? 'default' : 'outline'}
        className="w-full mt-4"
      >
        {selected ? 'Selected' : 'Select'}
      </Button>
    </ProductContainer>
  );
}

const ProductContainer = tw.div`h-full grid grid-rows-auto gap-2 p-3 md:p-4 border border-border rounded-md relative border-border cursor-pointer hover:shadow-lg transition-all duration-300`;
const ProductHeader = tw.div`flex flex-col gap-1 items-center text-center`;
const ProductPrice = tw.div`flex flex-col gap-1 justify-center mb-4`;
const ProductFeatures = tw.div`flex flex-col gap-1`;
const ProductFeatureRow = tw.div`flex gap-2 items-center ml-1 text-xs text-muted-foreground`;
